import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import PageGuide from "components/admin/PageGuide";
import PageCard from "components/admin/PageCard";
import ActionDialog from "components/admin/ActionDialog";
import AdminTable from "components/admin/AdminTable";
import GameIcon from "components/admin/GameIcon";
import TemplatePicker from "components/admin/TemplatePicker";
import { useConfirm } from "components/admin/Feedback";
import useConfirmChanges from "components/admin/confirmChanges";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { useAuth } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { formatNumber } from "utils/format";

/** Bỏ dấu + chữ thường để tìm tên không cần đúng dấu, giống SearchKey của máy chủ. */
const searchKey = (text) =>
  String(text || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/gi, "d")
    .toLowerCase()
    .trim();

const toValues = (settings) => Object.fromEntries(settings.map((s) => [s.key, s.value]));

function PetCell({ pet, id, extra }) {
  return (
    <MDBox display="flex" alignItems="center" gap={1}>
      <GameIcon path={pet?.icon} size={32} fallback="pets" />
      <MDBox lineHeight={1.2}>
        <MDTypography variant="button" fontWeight="medium" color={pet ? "dark" : "error"}>
          {pet ? pet.name : "Không tồn tại"}
        </MDTypography>
        <MDTypography variant="caption" color="text" display="block">
          #{id}
          {pet?.sky ? " · thiên đình" : ""}
        </MDTypography>
        {extra}
      </MDBox>
    </MDBox>
  );
}

PetCell.defaultProps = { pet: null, extra: null };
PetCell.propTypes = {
  pet: PropTypes.shape({ name: PropTypes.string, icon: PropTypes.string, sky: PropTypes.bool }),
  id: PropTypes.number.isRequired,
  extra: PropTypes.node,
};

function Sign({ children }) {
  return (
    <MDTypography variant="h5" color="text" textAlign="center">
      {children}
    </MDTypography>
  );
}

Sign.propTypes = { children: PropTypes.node.isRequired };

function TierDialog({ tier, onClose, onSaved }) {
  const [run, busy] = useRunner();
  const ask = useConfirmChanges();
  const [form, setForm] = useState({
    baseId: tier?.baseId ?? null,
    illusionId: tier?.illusionId ?? null,
    evolvedId: tier?.evolvedId ?? null,
  });
  const set = (patch) => setForm({ ...form, ...patch });
  const ready = form.baseId && form.illusionId && form.evolvedId;

  const submit = async () => {
    const yes = await ask({
      title: tier ? "Sửa dòng tiến hoá" : "Thêm dòng tiến hoá",
      isNew: !tier,
      before: tier,
      after: form,
      fields: [
        ["baseId", "Pet chính (mã)"],
        ["illusionId", "Pet ảo ảnh (mã)"],
        ["evolvedId", "Pet tiến hoá (mã)"],
      ],
      notes: ["Áp dụng từ lần tiến hoá kế; pet đã tiến hoá trước đó không đổi."],
    });
    if (!yes) return;
    const { ok, result } = await run(
      () =>
        tier
          ? api.put(`/api/admin/pet-evolve/tiers/${tier.tierId}`, form)
          : api.post("/api/admin/pet-evolve/tiers", form),
      tier ? "Đã sửa dòng tiến hoá" : "Đã thêm dòng tiến hoá"
    );
    if (ok) onSaved(result);
  };

  return (
    <ActionDialog
      open
      title={tier ? `Sửa dòng tiến hoá #${tier.tierId}` : "Thêm dòng tiến hoá"}
      description="Pet chính + pet ảo ảnh = pet tiến hoá. Mỗi pet chính chỉ có một dòng."
      onClose={onClose}
      onSubmit={submit}
      busy={busy}
      disabled={!ready}
      submitText={tier ? "Lưu" : "Thêm"}
      maxWidth="sm"
    >
      <TemplatePicker
        kind="pet"
        label="Pet chính (pet cơ bản)"
        value={form.baseId}
        onChange={(id) => set({ baseId: id })}
        fullWidth
      />
      <TemplatePicker
        kind="pet"
        label="Pet ảo ảnh"
        value={form.illusionId}
        onChange={(id) => set({ illusionId: id })}
        fullWidth
      />
      <TemplatePicker
        kind="pet"
        label="Pet tiến hoá (kết quả)"
        value={form.evolvedId}
        onChange={(id) => set({ evolvedId: id })}
        fullWidth
      />
    </ActionDialog>
  );
}

TierDialog.defaultProps = { tier: null };
TierDialog.propTypes = {
  tier: PropTypes.shape({
    tierId: PropTypes.number,
    baseId: PropTypes.number,
    illusionId: PropTypes.number,
    evolvedId: PropTypes.number,
  }),
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

function SettingInput({ setting, value, disabled, onChange }) {
  const changed = Number(value) !== setting.defaultValue;
  return (
    <MDInput
      fullWidth
      type="number"
      label={`${setting.label}${setting.unit ? ` (${setting.unit})` : ""}`}
      value={value}
      disabled={disabled}
      inputProps={{ min: setting.min, max: setting.max }}
      helperText={`${setting.doc ? `${setting.doc}. ` : ""}Mặc định ${formatNumber(
        setting.defaultValue
      )}${changed ? " (đã đổi)" : ""}`}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

SettingInput.propTypes = {
  setting: PropTypes.shape({
    label: PropTypes.string,
    unit: PropTypes.string,
    doc: PropTypes.string,
    min: PropTypes.number,
    max: PropTypes.number,
    defaultValue: PropTypes.number,
  }).isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  disabled: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

/** Điểm tiềm năng mỗi cấp của pet tiến hoá theo tổng sao, giống PetRealmManager.GetGymUpLevelAfterUpTier. */
function gymPerLevel(values, sky, stars) {
  const base = Number(values[sky ? "GYM_UP_TIER_BASE_SKY" : "GYM_UP_TIER_BASE_GROUND"]) || 0;
  const per = Number(values[sky ? "GYM_UP_TIER_STAR_SKY" : "GYM_UP_TIER_STAR_GROUND"]) || 0;
  const max = Number(values.GYM_UP_TIER_MAX_STAR) || 0;
  return base + per * Math.min(Math.max(stars, 0), max);
}

/** "Pet hạ giới: 0 sao 5 · 5 sao 10 · 10 sao 15 điểm/cấp" để xem nhanh công thức đang đặt. */
function gymSummary(values, sky) {
  const points = [0, 5, 10].map((stars) => `${stars} sao ${gymPerLevel(values, sky, stars)}`);
  return `Pet ${sky ? "thiên đình" : "hạ giới"}: ${points.join(" · ")} điểm/cấp`;
}

function PetEvolve() {
  const canEdit = useAuth().can(PERMISSIONS.PET_EVOLVE_EDIT);
  const evolve = useFetch(() => api.get("/api/admin/pet-evolve"), []);
  const [run, busy] = useRunner();
  const confirm = useConfirm();
  const [values, setValues] = useState(null);
  const [editing, setEditing] = useState(undefined);
  const [q, setQ] = useState("");

  const data = evolve.data;
  useEffect(() => {
    if (data) setValues(toValues(data.settings));
  }, [data]);

  const dirty =
    data && values && data.settings.some((s) => Number(values[s.key]) !== Number(s.value));

  const ask = useConfirmChanges();
  const saveSettings = async () => {
    const yes = await ask({
      title: "Lưu thông số tiến hoá pet",
      before: toValues(data.settings),
      after: values,
      fields: data.settings.map((st) => [st.key, st.label]),
      notes: ["Áp dụng từ lần tiến hoá kế."],
    });
    if (!yes) return;
    const changed = Object.fromEntries(
      data.settings
        .filter((s) => Number(values[s.key]) !== Number(s.value))
        .map((s) => [s.key, Number(values[s.key])])
    );
    const { ok, result } = await run(
      () => api.put("/api/admin/pet-evolve/settings", { values: changed }),
      "Đã lưu, áp dụng từ lần tiến hoá kế"
    );
    if (ok) evolve.setData(result);
  };

  const remove = async (tier) => {
    const ok = await confirm({
      title: `Xoá dòng tiến hoá #${tier.tierId}`,
      message: `${
        tier.basePet?.name || `#${tier.baseId}`
      } sẽ không tiến hoá được nữa. Pet đã tiến hoá của người chơi không bị ảnh hưởng.`,
      color: "error",
      confirmText: "Xoá",
    });
    if (!ok) return;
    const res = await run(
      () => api.del(`/api/admin/pet-evolve/tiers/${tier.tierId}`),
      "Đã xoá dòng tiến hoá"
    );
    if (res.ok) evolve.setData(res.result);
  };

  const rows = useMemo(() => {
    if (!data) return [];
    const key = searchKey(q);
    if (!key) return data.tiers;
    return data.tiers.filter((t) =>
      [
        t.basePet?.name,
        t.illusionPet?.name,
        t.evolvedPet?.name,
        t.baseId,
        t.illusionId,
        t.evolvedId,
      ]
        .map((x) => searchKey(x))
        .some((x) => x.includes(key))
    );
  }, [data, q]);

  const warningCount = data ? data.tiers.filter((t) => t.warnings.length > 0).length : 0;

  const groups = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    data.settings.forEach((s) => {
      if (!map.has(s.group)) map.set(s.group, []);
      map.get(s.group).push(s);
    });
    return [...map.entries()];
  }, [data]);

  const settingActions = (
    <MDBox display="flex" gap={1}>
      {canEdit && dirty && (
        <MDButton
          size="small"
          color="white"
          disabled={busy}
          onClick={() => setValues(toValues(data.settings))}
        >
          <Icon>undo</Icon>&nbsp;Bỏ sửa
        </MDButton>
      )}
      {canEdit && (
        <MDButton size="small" color="white" disabled={!dirty || busy} onClick={saveSettings}>
          <Icon>save</Icon>&nbsp;Lưu
        </MDButton>
      )}
      <Tooltip title="Tải lại (bỏ phần chưa lưu)">
        <MDButton size="small" color="white" onClick={() => evolve.reload()}>
          <Icon>refresh</Icon>
        </MDButton>
      </Tooltip>
    </MDBox>
  );

  const columns = [
    { key: "tierId", label: "#", width: 50, render: (t) => t.tierId },
    {
      key: "base",
      label: "Pet chính",
      render: (t) => <PetCell pet={t.basePet} id={t.baseId} />,
    },
    { key: "plus", label: "", width: 30, align: "center", render: () => <Sign>+</Sign> },
    {
      key: "illusion",
      label: "Pet ảo ảnh",
      render: (t) => (
        <PetCell
          pet={t.illusionPet}
          id={t.illusionId}
          extra={
            t.illusionPart && (
              <MDTypography variant="caption" color="text" display="block">
                Mảnh: {t.illusionPart.name} (#{t.illusionPart.itemId})
              </MDTypography>
            )
          }
        />
      ),
    },
    { key: "equals", label: "", width: 30, align: "center", render: () => <Sign>=</Sign> },
    {
      key: "evolved",
      label: "Pet tiến hoá",
      render: (t) => <PetCell pet={t.evolvedPet} id={t.evolvedId} />,
    },
    {
      key: "warnings",
      label: "Lưu ý",
      wrap: true,
      render: (t) =>
        t.warnings.length === 0 ? (
          <Icon color="success">check_circle</Icon>
        ) : (
          <MDBox display="flex" flexDirection="column" gap={0.5}>
            {t.warnings.map((w) => (
              <MDTypography key={w} variant="caption" color="warning">
                {w}
              </MDTypography>
            ))}
          </MDBox>
        ),
    },
  ];
  if (canEdit) {
    columns.push({
      key: "actions",
      label: "",
      width: 90,
      align: "right",
      render: (t) => (
        <MDBox display="flex" justifyContent="flex-end">
          <Tooltip title="Sửa">
            <IconButton size="small" onClick={() => setEditing(t)}>
              <Icon>edit</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Xoá">
            <IconButton size="small" color="error" onClick={() => remove(t)}>
              <Icon>delete</Icon>
            </IconButton>
          </Tooltip>
        </MDBox>
      ),
    });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="pet-evolve" />
        {evolve.error && <Alert severity="error">{evolve.error}</Alert>}
        {data && values && (
          <>
            <MDBox mb={3}>
              <PageCard
                title="Phí, điều kiện & điểm tiềm năng"
                subtitle="Cùng tham số với trang Cân bằng, sửa ở đâu cũng được"
                color="warning"
                actions={settingActions}
              >
                {groups.map(([group, settings]) => (
                  <MDBox key={group} mb={2}>
                    <MDTypography variant="h6" mb={1}>
                      {group}
                    </MDTypography>
                    <Grid container spacing={2}>
                      {settings.map((s) => (
                        <Grid item xs={12} sm={6} md={3} key={s.key}>
                          <SettingInput
                            setting={s}
                            value={values[s.key]}
                            disabled={!canEdit}
                            onChange={(v) => setValues({ ...values, [s.key]: v })}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </MDBox>
                ))}
                <MDBox display="flex" gap={1} flexWrap="wrap">
                  <Chip label={gymSummary(values, false)} />
                  <Chip label={gymSummary(values, true)} />
                </MDBox>
              </PageCard>
            </MDBox>

            <PageCard
              title="Bảng tiến hoá"
              subtitle={`${data.tiers.length} dòng${
                warningCount > 0 ? ` · ${warningCount} dòng cần xem lại` : ""
              }`}
              color="info"
              actions={
                <MDBox display="flex" gap={1}>
                  {canEdit && (
                    <MDButton size="small" color="white" onClick={() => setEditing(null)}>
                      <Icon>add</Icon>&nbsp;Thêm dòng
                    </MDButton>
                  )}
                </MDBox>
              }
              noPadding
            >
              <MDBox px={2} pt={2}>
                <MDInput
                  size="small"
                  label="Tìm theo tên hoặc id pet"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  sx={{ minWidth: 280 }}
                />
              </MDBox>
              <AdminTable columns={columns} rows={rows} rowKey="tierId" loading={busy} />
            </PageCard>
          </>
        )}
      </MDBox>
      {editing !== undefined && (
        <TierDialog
          tier={editing}
          onClose={() => setEditing(undefined)}
          onSaved={(result) => {
            evolve.setData(result);
            setEditing(undefined);
          }}
        />
      )}
      <Footer />
    </DashboardLayout>
  );
}

export default PetEvolve;
